import getWeglotLang, { classifyLangTag } from '@/services/getWeglotLang';
import { LanguageStore } from '@/services/weglotLanguageStore';

/**
 * Decide which language to translate into, given the URL and what the session
 * already remembers.
 *
 * The URL wins when it names a language, and that choice is remembered so it
 * survives the places the parameter cannot reach: a redirect through Google
 * Business Verification, or the full-page navigation the store performs on a
 * CSRF mismatch.
 *
 * An explicit `lang=en` clears the memory. With Weglot's switcher hidden that is
 * the only way for a user or support agent to get back to English, so it has to
 * mean "forget", not merely "not now".
 *
 * A malformed parameter leaves the remembered language alone rather than
 * resetting it, so one mistyped character in a shared link does not drop someone
 * mid-interview back into a language they cannot read.
 *
 * @returns the language to translate into, or null to stay in English.
 */
export default function resolveWeglotLanguage(
    location: { search: string, hash: string },
    store: LanguageStore,
): string | null {
    const requested = getWeglotLang(location);

    switch (requested.kind) {
        case 'language':
            store.write(requested.tag);
            return requested.tag;
        case 'english':
            store.clear();
            return null;
        case 'invalid':
            console.warn('Weglot: ignoring a malformed "lang" URL parameter.');
            break;
        case 'absent':
            break;
    }

    // Re-validated rather than trusted: the value is whatever was last put in
    // session storage, which the user can edit.
    const remembered = classifyLangTag(store.read());
    return remembered.kind === 'language' ? remembered.tag : null;
}
